import { GridCellProps } from '@progress/kendo-react-grid';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { trim, trimStart } from 'lodash';
import { SyntheticEvent, useCallback, useContext, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import GridCellBase from 'src/components/GridCellBase';
import { EditCellContext } from 'src/contexts/EditCellContext';
import useCompany from 'src/hooks/useCompany';
import useViewDepartment from 'src/hooks/useViewDepartments';
import useAddDepartmentApi from 'src/services/useAddDepartmentApi';

const RenameDepartmentCell = (props: GridCellProps) => {
  const [cellClick] = useContext(EditCellContext);
  const { latestClickedId, setLatestClickedId, refreshList } = cellClick?.(null);
  const { dataItem, onChange, dataIndex } = props;
  const { rowId, isNewDepartmentName, departmentId, editedName = '' } = dataItem;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const value = trim(dataItem.editedName);

  const openEdit = useCallback(() => {
    setLatestClickedId(rowId);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [rowId, setLatestClickedId]);

  const closeEdit = useCallback(() => setLatestClickedId(0), [setLatestClickedId]);
  const isEditable = rowId === latestClickedId;
  const { hasEditDepartmentPermission, departments, setDepartmentTableData } = useViewDepartment();

  const {
    company: { companyId },
  } = useCompany();

  const { addNewDepartment } = useAddDepartmentApi(Number(companyId));

  const handleBlur = useCallback(async () => {
    const originalValue = departments?.find((department) => department.name === value);
    if (!value) {
      toast.error('Please provide valid department name');
      setDepartmentTableData((prev) => prev.filter((item) => !item.isNewDepartmentName));
      return;
    } else if (value === originalValue?.name && departmentId !== originalValue?.departmentId) {
      toast.error('Department name must be unique');
      return;
    } else if (
      isNewDepartmentName ||
      (value !== originalValue?.name && departmentId !== originalValue?.departmentId)
    ) {
      await addNewDepartment(
        isNewDepartmentName ? [{ name: value }] : [{ name: value, departmentId: departmentId }]
      )
        .then((response) => {
          if (isNewDepartmentName) {
            toast.success('Department created successfully');
          } else {
            toast.success('Department name updated successfully');
          }
          refreshList();
        })
        .catch((err) => {
          toast.error(err);
          if (!isNewDepartmentName) {
            refreshList();
          }
          if (inputRef.current) {
            inputRef.current.focus();
          }
        });
    }

    closeEdit();
  }, [
    addNewDepartment,
    closeEdit,
    departmentId,
    departments,
    isNewDepartmentName,
    refreshList,
    setDepartmentTableData,
    value,
  ]);

  const handleChange = useCallback(
    (e: InputChangeEvent) => {
      onChange?.({
        dataIndex: dataIndex,
        dataItem: dataItem,
        field: 'editedName',
        syntheticEvent: e as unknown as SyntheticEvent,
        value: trimStart(String(e.value)),
      });
    },
    [dataIndex, dataItem, onChange]
  );

  useEffect(() => {
    if (inputRef?.current || isNewDepartmentName) {
      inputRef?.current?.focus();
    }
  }, [isNewDepartmentName]);

  return (
    <GridCellBase
      onClick={
        hasEditDepartmentPermission && (isNewDepartmentName || !isEditable) ? openEdit : undefined
      }
      title={hasEditDepartmentPermission ? 'Click to Edit' : undefined}
      {...props}
    >
      {hasEditDepartmentPermission && (isNewDepartmentName || isEditable) ? (
        <>
          <Input
            value={trimStart(editedName)}
            onBlur={handleBlur}
            onChange={handleChange}
            ref={inputRef}
          />
        </>
      ) : (
        <span>{editedName}</span>
      )}
    </GridCellBase>
  );
};

export default RenameDepartmentCell;
